import { useState, useEffect, useRef } from 'react';

const usePOCBudgets = (selectedPocs) => {
  // Track the previous selectedPocs to detect actual changes
  const prevSelectedPocsRef = useRef(null);
  
  // Initialize POC budgets from selected POCs
  const [pocBudgets, setPocBudgets] = useState(() => {
    const budgets = {};
    if (selectedPocs) {
      selectedPocs.forEach(poc => {
        if (poc.poc_budget !== null && poc.poc_budget !== undefined) {
          budgets[poc.poc_id] = poc.poc_budget;
        }
      });
    }
    return budgets;
  });

  // Update budgets when selectedPocs changes (from persistence or new POC additions)
  useEffect(() => {
    if (!selectedPocs) return;
    
    // Create a stringified version to compare (excluding the budget field to avoid circular updates)
    const currentPocIds = selectedPocs.map(p => p.poc_id).sort().join(',');
    const prevPocIds = prevSelectedPocsRef.current 
      ? prevSelectedPocsRef.current.map(p => p.poc_id).sort().join(',') 
      : '';
    
    // Only update if the POC list has actually changed (new POCs added/removed)
    if (currentPocIds !== prevPocIds) {
      setPocBudgets(prev => {
        const newBudgets = { ...prev }; // Preserve existing budget values
        
        // Add budgets for new POCs
        selectedPocs.forEach(poc => {
          if (!(poc.poc_id in newBudgets)) {
            // Only set if the POC has a budget value
            if (poc.poc_budget !== null && poc.poc_budget !== undefined) {
              newBudgets[poc.poc_id] = poc.poc_budget;
            }
          } else {
            // Update existing POC budget if it changed in selectedPocs
            if (poc.poc_budget !== null && poc.poc_budget !== undefined && poc.poc_budget !== newBudgets[poc.poc_id]) {
              newBudgets[poc.poc_id] = poc.poc_budget;
            }
          }
        });
        
        // Remove budgets for POCs that were removed
        const currentPocIdSet = new Set(selectedPocs.map(p => p.poc_id));
        Object.keys(newBudgets).forEach(pocId => {
          if (!currentPocIdSet.has(parseInt(pocId))) {
            delete newBudgets[pocId];
          }
        });
        
        return newBudgets;
      });
      
      prevSelectedPocsRef.current = selectedPocs;
    } else {
      // If POC list hasn't changed, only sync budgets from selectedPocs if they differ
      setPocBudgets(prev => {
        const newBudgets = { ...prev };
        let hasChanges = false;
        
        selectedPocs.forEach(poc => {
          const existingBudget = prev[poc.poc_id];
          const pocBudget = poc.poc_budget;
          
          // Only update if the budget in selectedPocs is different and not null/undefined
          if (pocBudget !== null && pocBudget !== undefined && pocBudget !== existingBudget) {
            newBudgets[poc.poc_id] = pocBudget;
            hasChanges = true;
          }
        });
        
        return hasChanges ? newBudgets : prev;
      });
    }
  }, [selectedPocs]);

  return {
    pocBudgets,
    setPocBudgets
  };
};

export default usePOCBudgets;
